import { isChunkLoadError } from 'src/utils/chunk-error-handler';

export type RouteErrorView = 'not-found' | 'error';

/**
 * Decide which UI a react-router route error should render. Only a genuine 404 route error
 * response is "not found"; every other thrown value (render crash, failed lazy chunk, loader
 * failure) must show the generic error UI so the user is not told a page does not exist.
 */
export const getRouteErrorView = (error: unknown): RouteErrorView => {
  if (
    typeof error === 'object' &&
    error !== null &&
    (error as { status?: unknown }).status === 404 &&
    (error as { internal?: unknown }).internal === true
  ) {
    return 'not-found';
  }
  return 'error';
};

export const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (typeof error === 'object' && error !== null) {
    const e = error as { statusText?: unknown; message?: unknown };
    const msg =
      typeof e.statusText === 'string'
        ? e.statusText
        : typeof e.message === 'string'
          ? e.message
          : '';
    return new Error(msg || 'Route error');
  }
  return new Error(typeof error === 'string' && error ? error : 'Route error');
};

export type RouteErrorRecovery = 'clear-caches-and-reload' | 'reload';

/**
 * How "Try Again" / "Refresh Page" should recover. A failed lazy chunk after a deploy is
 * usually a stale service-worker cache, which a plain reload would hit again, so those clear
 * caches first; every other error just reloads.
 */
export const getRouteErrorRecovery = (error: unknown): RouteErrorRecovery =>
  isChunkLoadError(toError(error)) ? 'clear-caches-and-reload' : 'reload';
